import { ReactElement, SyntheticEvent, useCallback } from 'react';
import { Stack } from '@mui/material';
import { TabContext } from '@mui/lab';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserTie as DriverIcon,
  faVectorSquare as LogisticZoneIcon,
  faStoreAlt as CompanyIcon,
} from '@fortawesome/free-solid-svg-icons';

import { TabData, TabPanelData } from '~globals/types';
import LoadingFallback from '~components/LoadingFallback';

import { DashboardVariantV2CarrierContentCollectTrackingPanelId } from '../../types';
import { useDashboardVariantV2CarrierContentCollectTrackingContext } from '../../Context';

import DashboardVariantV2CarrierContentCollectTabTrackingSelectionTabDrivers from './Drivers';
import DashboardVariantV2CarrierContentCollectTabTrackingSelectionTabZones from './Zones';
import DashboardVariantV2CarrierContentCollectTabTrackingSelectionTabCompanies from './Companies';

import {
  DashboardVariantV2CarrierContentCollectTabTrackingSelectionTabList,
  DashboardVariantV2CarrierContentCollectTabTrackingSelectionTab,
  DashboardVariantV2CarrierContentCollectTabTrackingSelectionTabsPanelContainer,
  DashboardVariantV2CarrierContentCollectTabTrackingSelectionTabPanel,
} from './styles';

const TABS_LIST: TabData<DashboardVariantV2CarrierContentCollectTrackingPanelId>[] =
  [
    {
      id: 'drivers',
      label: 'Por choferes',
      icon: <FontAwesomeIcon icon={DriverIcon} />,
    },
    {
      id: 'zones',
      label: 'Por zonas',
      icon: <FontAwesomeIcon icon={LogisticZoneIcon} />,
    },
    {
      id: 'companies',
      label: 'Por empresa',
      icon: <FontAwesomeIcon icon={CompanyIcon} />,
    },
  ];

// eslint-disable-next-line max-len
const TABS_LIST_PANELS: TabPanelData<DashboardVariantV2CarrierContentCollectTrackingPanelId>[] =
  [
    {
      id: 'drivers',
      panel:
        DashboardVariantV2CarrierContentCollectTabTrackingSelectionTabDrivers,
    },
    {
      id: 'zones',
      panel:
        DashboardVariantV2CarrierContentCollectTabTrackingSelectionTabZones,
    },
    {
      id: 'companies',
      panel:
        DashboardVariantV2CarrierContentCollectTabTrackingSelectionTabCompanies,
    },
  ];

const DashboardVariantV2CarrierContentCollectTabTrackingSelectionTabs =
  (): ReactElement => {
    const {
      activeTab: { tabId: activeTab, setTabId: setActiveTab },
      tabsData: { loading: loadingTabData },
    } = useDashboardVariantV2CarrierContentCollectTrackingContext();

    const handleChangeActiveTab = useCallback(
      (
        _e: SyntheticEvent,
        newValue: DashboardVariantV2CarrierContentCollectTrackingPanelId,
      ) => {
        setActiveTab(newValue);
      },
      [setActiveTab],
    );

    return (
      <Stack spacing={3} flex={1} sx={{ overflowY: 'hidden' }}>
        <TabContext value={activeTab}>
          <DashboardVariantV2CarrierContentCollectTabTrackingSelectionTabList
            onChange={handleChangeActiveTab}
          >
            {TABS_LIST.map(({ label, id, icon }) => (
              <DashboardVariantV2CarrierContentCollectTabTrackingSelectionTab
                key={`tracking-tab-${id}`}
                label={label}
                value={id}
                icon={icon}
                iconPosition="start"
              />
            ))}
          </DashboardVariantV2CarrierContentCollectTabTrackingSelectionTabList>

          <DashboardVariantV2CarrierContentCollectTabTrackingSelectionTabsPanelContainer>
            {TABS_LIST_PANELS.map(({ id, panel: Panel }) => (
              <DashboardVariantV2CarrierContentCollectTabTrackingSelectionTabPanel
                key={`tracking-tab-panel-${id}`}
                value={id}
              >
                {loadingTabData && <LoadingFallback />}

                {!loadingTabData && <Panel />}
              </DashboardVariantV2CarrierContentCollectTabTrackingSelectionTabPanel>
            ))}
          </DashboardVariantV2CarrierContentCollectTabTrackingSelectionTabsPanelContainer>
        </TabContext>
      </Stack>
    );
  };

export default DashboardVariantV2CarrierContentCollectTabTrackingSelectionTabs;
